import { Id } from '@/infra/id'
import type {
  IamFeatureOptions,
  ProDetailsViewModelFactory,
} from '@/features/iam'
import type { MemberStore } from '@/features/iam/members/MemberStore'
import type { SkillStore } from '@/features/iam/skills/SkillStore'
import type { RoleStore } from '@/features/iam/roles/RoleStore'
import type { TeamStore } from '@/features/iam/teams/TeamStore'
import type { TeamMemberStore } from '@/features/iam/teams/TeamMemberStore'
import type { TeamRoleStore } from '@/features/iam/teams/TeamRoleStore'
import type { InvitationStore } from '@/features/iam/invitations/InvitationStore'
import type { ProviderStore } from '@/features/iam/providers/ProviderStore'
import type { UserDataStore } from '@/features/iam/users/UserDataStore'
import { TeamsViewModel } from '@/features/iam/teams/TeamsViewModel'
import { TeamViewModel } from '@/features/iam/teams/TeamViewModel'
import type { Team } from '@/features/iam/teams/Team'
import { ProfileViewModel } from '@/features/iam/users/ProfileViewModel'
import { ValueRetrievablePromise } from '@/utils/value-retrievable-promise'
import { memoize } from '@/utils/memoize-util'
import type { Member } from '@/features/iam/members/Member'
import { ProDetailsViewModel } from '@/features/iam/providers/ProDetails/ProDetailsViewModel'

/**
 * Options for {@link InternalIamFeature}: the feature's options plus the
 * stores shared with the eagerly built part of iam.
 */
export interface InternalIamFeatureOptions extends IamFeatureOptions {
  memberStore: MemberStore
  skillStore: SkillStore
  roleStore: RoleStore
  teamStore: TeamStore
  teamMemberStore: TeamMemberStore
  teamRoleStore: TeamRoleStore
  invitationStore: InvitationStore
  iamProviderStore: ProviderStore
  userDataStore: UserDataStore
}

/**
 * Acquires the teams view model of the given member's workspace.
 */
export type AcquireTeamsViewModelFn = (member: Member) => TeamsViewModel

/**
 * Acquires the view model of the given team, acting as the given member.
 */
export type AcquireTeamViewModelFn = (
  member: Member,
  team: Team,
  signal?: AbortSignal,
) => Promise<TeamViewModel>

/**
 * Acquires the profile view model of the given member's workspace.
 */
export type AcquireProfileViewModelFn = (
  member: Member,
  signal?: AbortSignal,
) => Promise<ProfileViewModel>

export function InternalIamFeature(options: InternalIamFeatureOptions) {
  // Each workspace keeps its pages' state for the rest of the session.
  const acquireTeamsViewModel: AcquireTeamsViewModelFn = memoize(
    (member: Member) =>
      new TeamsViewModel(
        member.workspace,
        options.teamMemberStore,
        options.teamStore,
        () => member,
      ),
    { keySelector: ([member]) => Id.assert(member.workspace.id) },
  )

  // A team keeps its page's state for the rest of the session.
  const acquireTeamViewModel: AcquireTeamViewModelFn = memoize(
    async (member: Member, team: Team, signal?: AbortSignal) => {
      const createTeamPaymentsViewModel =
        await options.createTeamPaymentsViewModel
      signal?.throwIfAborted()

      return new TeamViewModel(
        team,
        options.teamStore,
        () => member,
        options.teamRoleStore,
        options.teamMemberStore,
        options.invitationStore,
        options.isBillingEnabled,
        createTeamPaymentsViewModel,
        options.newAlert,
        options.createAlertErrorHandler,
      )
    },
    {
      keySelector: ([, team]) => Id.assert(team.id),
      promise: ([, , signal]) => signal,
    },
  )

  const acquireProfileViewModel: AcquireProfileViewModelFn = memoize(
    async (member: Member, signal?: AbortSignal) => {
      const {
        createProfilePaymentMethodsViewModel,
        createProfilePayoutMethodViewModel,
        createStripeConnectCallbackModalViewModel,
      } = await ValueRetrievablePromise.props({
        createProfilePaymentMethodsViewModel:
          options.createProfilePaymentMethodsViewModel,
        createProfilePayoutMethodViewModel:
          options.createProfilePayoutMethodViewModel,
        createStripeConnectCallbackModalViewModel:
          options.createStripeConnectCallbackModalViewModel,
      })
      signal?.throwIfAborted()

      return new ProfileViewModel(
        member,
        options.newAlert,
        options.createAlertErrorHandler,
        options.createAlertRetryWrapper,
        options.memberStore,
        options.skillStore,
        options.roleStore,
        options.fetchNotificationPreferences,
        options.saveNotificationPreferences,
        options.isBillingEnabled,
        options.isPayoutsEnabled,
        createProfilePaymentMethodsViewModel,
        createProfilePayoutMethodViewModel,
        createStripeConnectCallbackModalViewModel,
        options.navigate,
        options.iamProviderStore,
        options.userDataStore,
      )
    },
    {
      keySelector: ([member]) => Id.assert(member.workspace.id),
      promise: ([, signal]) => signal,
    },
  )

  const createProDetailsViewModel: ProDetailsViewModelFactory = (
    userPublicId,
    editable,
  ) =>
    new ProDetailsViewModel(
      options.newAlert,
      options.createAlertErrorHandler,
      options.iamProviderStore,
      userPublicId,
      editable,
    )

  return {
    acquireTeamsViewModel,
    acquireTeamViewModel,
    acquireProfileViewModel,
    createProDetailsViewModel,
  }
}
